// clientFormFisaService.config.js
import React from "react";
import { DOC_TYPES, LOCALSTORAGE_ITEMS, TYPES } from "../../../util/Constants";
import * as StorageService from "../../../services/StorageServices";
import { FormattedDate } from "react-intl";
import { PATH } from "../../../routes/routes";
import ClientFormFisaServiceDetails from "../ClientFormFisaServiceDetails";
import StatusBadge from "../../../components/StatusBadge";
import { PAGE_SIZE } from "../../../config/ClientConfig";
import {
  getAllClientFormsHandler,
  getMyClientFormsHandler,
  deleteClientFormByIdHandler,
} from "../../../handlers/clientFormsHandlers/HandlerClientForms";

const FISA_SERVICE_PREFIX = "FISA_SERVICE_";

const getCompanyInfo = () => {
  const raw = StorageService.getItem(LOCALSTORAGE_ITEMS.COMPANY_INFO);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch(err) {
    console.error("getCompanyInfo", err);
    return null;
  }
};

const getAccountType = () => {
  return StorageService.getItem(LOCALSTORAGE_ITEMS.TYPE) || "Unknown";
};

function FormattedDateCell({ value }) {
  return (
    <FormattedDate
      value={value}
      hour="numeric"
      minute="numeric"
      hour12={false}
      day="numeric"
      month="short"
      year="numeric"
      timeZone={StorageService.getItem(LOCALSTORAGE_ITEMS.TIMEZONE)}
      locale={StorageService.getItem(LOCALSTORAGE_ITEMS.LOCALE)}
    />
  );
}

const resolveFisaServiceFormId = async () => {
  const companyInfo = getCompanyInfo();
  const list = companyInfo?.availableClientForms || [];
  const match = list.find((x) => typeof x === "string" && x.startsWith(FISA_SERVICE_PREFIX));
  return match || null;
};

const FIELD_KEYS = {
  numeClient: "numeClient",
  clientPhone: "clientPhone",
  productDescription: "productDescription",
  operatiuneValues: "operatiuneValues",
  operatiuneAltulText: "operatiuneAltulText",
  pretTotalLei: "pretTotalLei",
};

const getFieldArr = (fields, key) => {
  const f = (fields || []).find((x) => x?.id === key || x?.name === key);
  if (!f) return [];
  if (Array.isArray(f.value)) return f.value;
  if (typeof f.value === "undefined" || f.value === null) return [];
  return [f.value];
};

export const clientFormFisaServiceConfig = ({ companyId }) => {
  const type = getAccountType();
  const isAdmin = type === TYPES.ADMIN;

  const columnsBase = [
    {
      Header: "#",
      accessor: "rowNo",
      isSortable: false,
      disableFilters: true,
      filterable: false,
      Cell: (row) => row.value,
    },

    { Header: "Cumpărător", accessor: "clientName", isSortable: false, hasIcon: true },
    { Header: "Telefon", accessor: "clientPhone", isSortable: false },
    { Header: "Locație", accessor: "locationName", isSortable: false },

    {
      Header: "Data creării",
      accessor: "created",
      isSortable: true,
      disableFilters: true,
      Cell: (row) => (
        <FormattedDate
          value={row.value}
          month="short"
          year="numeric"
          hour="numeric"
          minute="numeric"
          hour12={false}
          day="numeric"
        />
      ),
    },

    {
      Header: "Status",
      accessor: "status",
      isSortable: false,
      Cell: (row) => <StatusBadge status={row.value} />,
    },

    { Header: "Chitanță", accessor: "receipt", isSortable: false },
  ];

  const columns = isAdmin
    ? [...columnsBase, { Header: "Actions", accessor: "actions", isSortable: false, filterable: false }]
    : columnsBase;

  return {
    path: PATH.SERVICE_FISE(companyId),
    label: "Fișă service",
    value: DOC_TYPES?.SERVICE_FISE ?? DOC_TYPES?.FISA ?? "FISA",
    headerTitle: "Fișe service",
    addBtnText: "Adaugă",
    pageSize: PAGE_SIZE,
    DetailsComponent: ClientFormFisaServiceDetails,

    getAll: async ({ createdFrom, createdTo, page = 0, sort, locationsIds } = {}) => {
      const formId = await resolveFisaServiceFormId();

      if (!formId) {
        return {
          data: {
            error: null,
            items: [],
            page: 0,
            size: PAGE_SIZE,
            totalElements: 0,
            totalPages: 0,
            first: true,
            last: true,
            empty: true,
          },
        };
      }

      const params = { companyId, formId, page, size: PAGE_SIZE, sort: sort || ["created,DESC"] };
      if (createdFrom) params.createdFrom = createdFrom;
      if (createdTo) params.createdTo = createdTo;
      if (locationsIds && locationsIds.length) params.locationsIds = locationsIds;

      if (type === TYPES.WORKER) {
        return getMyClientFormsHandler(params);
      }

      return getAllClientFormsHandler(params);
    },

    del: (id) => deleteClientFormByIdHandler(id),

    columns,

    mapList: (items) =>
      (items || []).map((cf, idx) => {
        const fields = cf?.fields || [];

        const numeClient = (getFieldArr(fields, FIELD_KEYS.numeClient)[0] || "").trim();
        const clientPhone = (getFieldArr(fields, FIELD_KEYS.clientPhone)[0] || "").trim();
        const productDescription = (getFieldArr(fields, FIELD_KEYS.productDescription)[0] || "").trim();

        const opValues = getFieldArr(fields, FIELD_KEYS.operatiuneValues) || [];
        const opAlt = (getFieldArr(fields, FIELD_KEYS.operatiuneAltulText)[0] || "").trim();
        const operatiuneText = [...opValues, opAlt].filter(Boolean).join(", ");

        const pretLei = (getFieldArr(fields, FIELD_KEYS.pretTotalLei)[0] || "").trim();

        return {
          ...cf,
          rowNo: idx + 1,
          status: (cf?.status || "PENDING").toUpperCase(),

          locationId: String(cf?.location?.id || ""),
          locationName: cf?.location?.name || "-",
          location: cf?.location || null,

          created: cf?.created || new Date(),

          clientName: cf?.clientName || numeClient || "",
          clientPhone: cf?.clientPhone || clientPhone || "",

          productDescription: productDescription || "",
          operatiuneText,
          pretLei,
          receipt: String(cf?.receipt || ""),
        };
      }),

    pickListFromResponse: (data) => data?.items || data?.content || data?.page?.content || [],

    deleteAlertTitle: "Ștergere fișă service",
    deleteAlertText: "Ești sigur că vrei să ștergi fișa service?",
    getDeleteLabel: (row) => (row?.clientName ? `${row.clientName} (${row.clientPhone || "-"})` : row?.id),
  };
};